import React, { useEffect, useRef } from 'react';
import { Box, HStack, Input, RadioCard, SimpleGrid, Text } from '@chakra-ui/react';
import type { DonationFrequency } from '@kuow/core';

interface AmountSelectItem {
  id: number;
  value: string;
  label: string;
}

interface AmountSelectProps {
  items: AmountSelectItem[];
  selectedValue: string;
  onChange: (value: string | null) => void;
  customAmountValue: string | number;
  onCustomAmountChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  frequency?: DonationFrequency;
}

export const AmountSelect: React.FC<AmountSelectProps> = ({
  items,
  selectedValue,
  onChange,
  customAmountValue,
  onCustomAmountChange,
  frequency = 'one-time',
}) => {
  const customInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selectedValue === 'custom' && customInputRef.current) {
      customInputRef.current.focus();
    }
  }, [selectedValue]);

  const isMonthly = frequency === 'monthly';
  const customAmountString = typeof customAmountValue === 'number' ? String(customAmountValue) : customAmountValue;
  const customAmountLength = Math.max(2, customAmountString.length);
  const customAmountWidth = `${customAmountLength}ch`;

  return (
    <RadioCard.Root
      value={selectedValue}
      onValueChange={(details) => onChange(details.value)}
      size="md"
      variant="solid"
      colorPalette="river"
      align="center"
    >
      <Box w="full" display="flex" flexDirection="column" gap={1.5}>
        <SimpleGrid columns={2} gap={1.5}>
          {items.map((item) => (
            <RadioCard.Item key={item.value} value={item.value} cursor="pointer">
              <RadioCard.ItemHiddenInput />
              <RadioCard.ItemControl w="full" display="flex" alignItems="center" justifyContent="center">
                <RadioCard.ItemText fontSize="md" fontWeight="medium" textAlign="center" lineHeight="1.1">
                  {item.label}
                </RadioCard.ItemText>
              </RadioCard.ItemControl>
            </RadioCard.Item>
          ))}
        </SimpleGrid>

        <RadioCard.Item value="custom" cursor="pointer" w="full">
          <RadioCard.ItemHiddenInput />
          <RadioCard.ItemControl w="full" display="flex" alignItems="center" justifyContent="center">
            <RadioCard.ItemText
              display={selectedValue === 'custom' ? 'none' : 'flex'}
              alignItems="center"
              justifyContent="center"
              w="full"
              fontSize="md"
              fontWeight="medium"
              textAlign="center"
              lineHeight="1.1"
            >
              Other
            </RadioCard.ItemText>
            <HStack
              display={selectedValue === 'custom' ? 'flex' : 'none'}
              gap={0.5}
              align="center"
              justify="center"
              w="full"
              px={0}
            >
              <Text fontSize="md" fontWeight="medium" lineHeight="1.1">
                $
              </Text>
              <Input
                ref={customInputRef}
                type="number"
                placeholder=""
                value={customAmountValue}
                onChange={onCustomAmountChange}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                min={1}
                step={0.01}
                border="none"
                outline="none"
                bg="transparent"
                px={0}
                h="auto"
                fontSize="md"
                fontWeight="medium"
                lineHeight="1.1"
                w={customAmountWidth}
                minW={isMonthly ? 15 : 20}
                textAlign="left"
                _focus={{ boxShadow: 'none', outline: 'none' }}
                css={{
                  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '&[type=number]': {
                    MozAppearance: 'textfield',
                  },
                }}
              />
              {isMonthly && (
                <Text fontSize="md" fontWeight="medium" lineHeight="1.1">
                  /mo
                </Text>
              )}
            </HStack>
          </RadioCard.ItemControl>
        </RadioCard.Item>
      </Box>
    </RadioCard.Root>
  );
};


